import { IconX, IconPlus, IconListCheck } from '@tabler/icons-react';
import { Form, Modal, Row, Col } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { customStyles } from '../../../../Utils/helpers/commonhelper';
import { PostRequest } from '../../../../Utils/hooks/ApiHandler';
import useLocalStorageState from 'use-local-storage-state';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Cleaned up Schema to match a "Todo"
const todoSchema = z.object({
    title: z.string().min(1, "Title is required!"),
    priority: z.string().min(1, "Priority is required!"),
    due_date: z.string().min(1, "Due date is required!"),
    due_time: z.string().min(1, "Due time is required!"),
    reminderBefore: z.string().optional(),
});

export const AddTodoModal = ({ show, onClose, onSuccess }) => {
    const [customer, setCustomer] = useLocalStorageState("customer", {
        token: {},
    })
    const customer_id = customer?.customer_details?.customer_id
    const token = customer?.token?.authorization
    const rf = customer?.token?.authentication
    const headers = {
        Authorization: token,
        Authentication: rf,
    }
    const navigate = useNavigate()
    const extras = { customer, setCustomer, rf, navigate }

    const priorityOptions = [
        { value: "High", label: "High" },
        { value: "Medium", label: "Medium" },
        { value: "Low", label: "Low" }
    ];

    const reminderOptions = Array.from({ length: 6 }, (_, i) => {
        const value = ((i + 1) * 5).toString()
        return {
            value,
            label: `${value} minutes`
        }
    })

    // React Hook Form setup with Zod validation
    const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD
    // Function to get current time in HH:MM format
    const getCurrentTime = () => {
        const now = new Date()
        return now.toTimeString().slice(0, 5)
    }
    const { register, handleSubmit, control, formState: { errors }, reset } = useForm({
        resolver: zodResolver(todoSchema),
        defaultValues: {
            priority: "Low",
            due_time: getCurrentTime(),
            due_date: today,
            reminderBefore: "5",
        }
    });

    const onSubmit = async (data) => {

        try {
            const payload = {
                customer_id: customer_id,
                user_login_id: customer?.customer_details?.id,
                priority: data.priority?.toLowerCase(),
                ...data
            }

            const res = await PostRequest('taskboard/insertTodo/', payload, false, { headers }, extras)
            if (res.status === "fail" || res.success === false) {
                toast.error(res.message);
                return;
            }
            toast.success(res.message)
            if (onSuccess) onSuccess()
            reset()
        } catch (error) {
            return
        }

    };


    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            contentClassName="border-0 shadow-lg rounded-4 "
            size="md"
        >
            <Modal.Header closeButton className="px-3 pt-3 pb-2 bg-light ">
                <div className="d-flex align-items-center gap-2">
                    <div
                        className="d-flex align-items-center justify-content-center rounded-2"
                        style={{
                            background: "linear-gradient(135deg, #7a3a5e 0%, #4f243c 100%)",
                            width: "34px",
                            height: "34px",
                            boxShadow: "0 4px 10px rgba(122, 58, 94, 0.2)"
                        }}
                    >
                        <IconListCheck size={18} color="white" stroke={3} />
                    </div>
                    <div className="d-flex flex-column">
                        <span className="fw-bold text-dark" style={{ fontSize: "14px", lineHeight: "1.2" }}>
                            Create New Todo
                        </span>
                        <small className="text-muted" style={{ fontSize: "11px" }}>Add a task to your schedule</small>
                    </div>
                </div>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <Form className='form-container' onSubmit={handleSubmit(onSubmit)}>
                    {/* Task Title */}
                    <Form.Group className="mb-3 flex-column focus-z-index position-relative">
                        <Form.Label className="small fw-semibold text-secondary " style={{ fontSize: '11px', letterSpacing: '0.5px' }}>
                            Task Title <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            {...register("title")}
                            type="text"
                            placeholder="e.g. Follow up with Sarah"
                            className={`form-control-sm border-2 ${errors.title ? 'is-invalid' : ''}`}
                            style={{ padding: '10px 12px', fontSize: '14px' }}
                        />
                        {errors.title && <div className="invalid-feedback small">{errors.title.message}</div>}
                    </Form.Group>

                    {/* Date and Priority Row */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3 flex-column focus-z-index position-relative">
                                <Form.Label className="small fw-semibold text-secondary " style={{ fontSize: '11px' }}>
                                    Due Date <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    {...register("due_date")}
                                    type="date"
                                    className={`form-control-sm ${errors.due_date ? 'is-invalid' : ''}`}
                                />
                                {errors.due_date && <div className="invalid-feedback small">{errors.due_date.message}</div>}
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3 flex-column focus-z-index position-relative">
                                <Form.Label className="small fw-semibold text-secondary " style={{ fontSize: '11px' }}>
                                    Priority
                                </Form.Label>
                                <Controller
                                    name="priority"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            styles={customStyles}
                                            options={priorityOptions}
                                            className="react-select-sm"
                                            classNamePrefix="react-select"
                                            onChange={(selected) => field.onChange(selected.value)}
                                            value={priorityOptions.find(opt => opt.value === field.value)}
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Due Time & Reminder */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3 flex-column focus-z-index position-relative">
                                <Form.Label className="small fw-semibold text-secondary " style={{ fontSize: '11px' }}>
                                    Due Time <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    {...register("due_time")}
                                    type="time"
                                    className={`form-control-sm ${errors.due_time ? 'is-invalid' : ''}`}
                                />
                                {errors.due_time && <div className="invalid-feedback small">{errors.due_time.message}</div>}
                            </Form.Group>
                        </Col>

                        <Col md={6}>
                            <Form.Group className="mb-3 flex-column focus-z-index position-relative">
                                <Form.Label className="small fw-semibold text-secondary " style={{ fontSize: '11px' }}>
                                    Reminder Before(in min)
                                </Form.Label>

                                <Controller
                                    name="reminderBefore"
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            styles={customStyles}
                                            options={reminderOptions}
                                            className="react-select-sm"
                                            classNamePrefix="react-select"
                                            placeholder="Select or type minutes"
                                            isClearable
                                            isSearchable
                                            onInputChange={(inputValue, { action }) => {
                                                if (action === 'input-change') {
                                                    field.onChange(inputValue)
                                                }
                                            }}
                                            onChange={(selected) => field.onChange(selected?.value || '')}
                                            value={
                                                reminderOptions.find(opt => opt.value === field.value)
                                                    ? reminderOptions.find(opt => opt.value === field.value)
                                                    : field.value
                                                        ? { value: field.value, label: `${field.value} minutes` }
                                                        : null
                                            }
                                        />
                                    )}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-default-outline px-3 fw-medium text-secondary"
                            style={{ fontSize: "13px" }}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 fw-semibold shadow-sm btn-default transition-all"
                            style={{
                                color: "#fff",
                                border: "none",
                                fontSize: "14px",
                                borderRadius: "6px",
                            }}
                        >
                            <IconPlus size={18} className="me-1" />
                            Create Todo
                        </button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};